import React, { useState, useEffect, useRef, forwardRef, useImperativeHandle, useCallback } from "react";
import { useLocation } from "react-router-dom";
import { useSwipeable } from "react-swipeable";

const ImageSlider = ({ images, ...props }) => {
	const location = useLocation();
	const [currImg, setCurrImg] = useState(0);
	const imagesRefs = useRef([]);
	const handlers = useSwipeable({
		onSwipedRight: () => changeImage(null, null, true, -1),
		onSwipedLeft: () => changeImage(null, null, true, 1)
	});

	if (imagesRefs.current.length !== images.length) {
		imagesRefs.current = Array(images.length)
			.fill()
			.map((_, i) => imagesRefs.current[i] || React.createRef());
	}

	const changeImage = useCallback(
		(target = null, index = null, animation = true, direction = null) => {
			const currImageRef = imagesRefs.current[currImg].current;
			let dir = direction || 1; //1 => prawo ; -1 => lewo
			let newValue;

			if (index === currImg) return;
			if (target != null && direction == null) {
				if (target.current.classList.contains("arrow--left")) dir = -1;
			}

			if (target != null || direction != null) {
				newValue = currImg + dir;
				if (newValue < 0) newValue = images.length - 1;
				if (newValue > images.length - 1) newValue = 0;
				if (newValue < currImg) dir = -1;
				if (currImg === 0) dir = 1;
			}

			if (index != null) {
				newValue = index;
				if (newValue < currImg) dir = -1;
			}

			if (animation) {
				currImageRef.animate(imagesRefs.current[newValue].current, dir);
			} else {
				currImageRef.toggleClass("hide");
				currImageRef.toggleClass("hide");
			}

			setCurrImg(newValue);
		},
		[currImg, images.length]
	);

	useEffect(() => {
		setCurrImg(0);
	}, [location]);

	return (
		<div className="img_slider">
			<Arrows show={images.length > 1} handler={changeImage} />
			<div className="img_slider__container" {...handlers}>
				{images.map((image, i) => (
					<Image source={image} index={i} key={i} ref={imagesRefs.current[i]} />
				))}
			</div>
			<Indicator currImg={currImg} images={images} changeImage={changeImage} />
		</div>
	);
};

const Image = forwardRef(({ index, source }, ref) => {
	const [className, setClassName] = useState("");
	const image = useRef();

	useEffect(() => {
		if (index !== 0) setClassName("hide");
	}, [index]);

	const animate = (nextImageRef, dir) => {
		nextImageRef.removeClass("hide");

		if (dir < 0) {
			image.current.classList.add("slide_right");
			nextImageRef.addClass("slide_right", "curr_image");
		} else {
			image.current.classList.add("slide_left");
			nextImageRef.addClass("slide_left");
		}

		setTimeout(() => {
			image.current.classList.add("hide");
			image.current.classList.remove("slide_left", "slide_right");
			nextImageRef.removeClass("slide_left");
			nextImageRef.removeClass("slide_right");
			nextImageRef.removeClass("hide");
		}, 1000);
	};

	useImperativeHandle(ref, () => ({
		addClass: classToAdd => {
			image.current.classList.add(classToAdd);
		},
		removeClass: classToRemove => {
			image.current.classList.remove(classToRemove);
		},
		toggleClass: classToToggle => {
			image.current.classList.toggle(classToToggle);
		},
		DOM: () => image.current,
		animate: animate
	}));

	return <img src={source} alt="" className={className} ref={image} />;
});

const Arrows = ({ show, handler }) => {
	const arrowLeft = useRef();
	const arrowRight = useRef();

	if (!show) return <></>;
	return (
		<div className="img_slider__arrows">
			<i
				className="fas fa-chevron-left arrow arrow--left"
				ref={arrowLeft}
				onClick={() => {
					handler(arrowLeft);
				}}
			></i>
			<i
				className="fas fa-chevron-right arrow arrow--right"
				ref={arrowRight}
				onClick={() => {
					handler(arrowRight);
				}}
			></i>
		</div>
	);
};

const Indicator = ({ currImg, images, changeImage }) => {
	const dotsRefs = useRef([]);

	if (dotsRefs.current.length !== images.length) {
		dotsRefs.current = Array(images.length)
			.fill()
			.map((_, i) => dotsRefs.current[i] || React.createRef());
	}

	const toggleClass = useCallback(
		ref => {
			ref.current.classList.remove("fas");

			if (Number(ref.current.getAttribute("data-key")) === currImg) {
				ref.current.classList.remove("far");
				ref.current.classList.add("fas");
				return;
			}

			ref.current.classList.add("far");
		},
		[currImg]
	);

	useEffect(() => {
		dotsRefs.current.forEach(ref => toggleClass(ref));
	}, [currImg, images, toggleClass]);

	return (
		<div className="img_slider__indicators">
			{images.map((v, i) => (
				<i
					className="far fa-circle indicator"
					key={i}
					data-key={i}
					ref={dotsRefs.current[i]}
					onClick={() => {
						changeImage(null, i);
					}}
				></i>
			))}
		</div>
	);
};

export default ImageSlider;
